import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router'

// mui
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  CircularProgress
} from '@mui/material'

// gql
import { useMutation } from '@apollo/client'

const DeleteObjectWithModal = ({ 
  id, 
  mutation, 
  deleteLabel, 
  deleteTitle, 
  deleteMessage, 
  successObjectKey,
  entityName = 'Item',
  redirectPath = null // Optional - only use if specific redirect needed
}) => {
    const history = useHistory()
    const location = useLocation()
    const [deleteMutation, { loading }] = useMutation(mutation)
    const [showModal, setShowModal] = useState(false)

    const handleCloseModal = () => {
        if (!loading) {
            setShowModal(false)
        }
    }

    const onConfirmClick = () => {
        deleteMutation({ variables: { id } })
        .then(result => {
            const response = result.data?.[successObjectKey]
            
            if (response?.didDelete) {
                // SUCCESS: Use backend message or fallback
                const successMessage = response.message || `${entityName} deleted successfully`
                const message = {
                    type: 'success',
                    text: successMessage,
                    timestamp: Date.now()
                }
                
                if (redirectPath) {
                    // Use custom redirect path if provided
                    history.push(redirectPath, { notification: message })
                } else {
                    // Default behavior: go back to previous page
                    history.goBack()
                    // Show message on the previous page
                    setTimeout(() => {
                        history.replace(window.location.pathname, { 
                            notification: message 
                        })
                    }, 100)
                }
            } else {
                // FAILURE: Use backend error message
                const errorMessage = response?.message || `Failed to delete ${entityName}`
                const errorNotification = {
                    type: 'error',
                    text: errorMessage,
                    timestamp: Date.now()
                }
                // Stay on current page but show backend error
                history.replace(location.pathname, { 
                    notification: errorNotification 
                })
                setShowModal(false)
            }
        })
        .catch(error => {
            // GraphQL or network errors
            const errorMessage = error.message || `Error deleting ${entityName}`
            const errorNotification = {
                type: 'error',
                text: errorMessage,
                timestamp: Date.now()
            }
            history.replace(location.pathname, { 
                notification: errorNotification 
            })
            setShowModal(false)
        })
    }

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setShowModal(true)}
                color="error"
                disabled={loading}
            >
                {deleteLabel}
            </Button>

            <Dialog
                open={showModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle color="error">{deleteTitle}</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {deleteMessage}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button 
                        onClick={handleCloseModal} 
                        disabled={loading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={onConfirmClick} 
                        color="error"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default DeleteObjectWithModal
